/**
 * AccountMenu.jsx
 * ===============
 * Menu de conta no rodape do GlobalSidebar (C3 visao USER).
 *
 * ESTRUTURA:
 *   Estado fechado (sempre visivel no rodape):
 *     [avatar 32px] [Nome do usuario]
 *                   [Nome do clube]
 *                   [4 de 21.250 · plano do clube]
 *
 *   Estado aberto (dropdown ASCENDENTE — abre PRA CIMA):
 *     - Cabecalho: avatar grande + nome + email
 *     - Ver Perfil       (/perfil ou /configuracoes)
 *     - Meu consumo      (/meu-consumo) — pagina completa C3
 *     - Configuracoes    (/configuracoes)
 *     - Ajuda e Suporte  (/contato)
 *     - Idioma           (consolida toggle PT/EN que estava solto no sidebar)
 *     - separador
 *     - Sair
 *
 * UI_PADRAO:
 *   - Cinza-base, sem cor decorativa
 *   - lucide-react outline
 *   - font-medium/font-semibold (sem 700+)
 *   - Sentence case
 *   - "X de Y" sem cor (eh info, nao alerta)
 *   - Botao Sair: cinza ate hover -> vermelho dessaturado #a93333
 *
 * Consome /api/me/usage via usageService.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  ChevronUp,
  Activity,
  Settings,
  HelpCircle,
  Globe,
  LogOut,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSportsLang } from '../../contexts/SportsLangContext';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import { getMyUsage, formatUsageRatio } from '../../services/usageService';

const I18N = {
  pt: {
    viewProfile: 'Ver perfil',
    myUsage: 'Meu consumo',
    settings: 'Configurações',
    help: 'Ajuda e suporte',
    language: 'Idioma',
    signOut: 'Sair',
    confirmLogout: 'Tem certeza que deseja sair?',
    loading: 'Carregando…',
    noClub: 'Sem clube vinculado',
    clubUnavailable: '—',
    planSuffix: '· plano do clube',
    planNoLimit: '· limite por usuário não definido',
    defaultName: 'Usuário',
  },
  en: {
    viewProfile: 'View profile',
    myUsage: 'My usage',
    settings: 'Settings',
    help: 'Help & support',
    language: 'Language',
    signOut: 'Sign out',
    confirmLogout: 'Are you sure you want to sign out?',
    loading: 'Loading…',
    noClub: 'No club linked',
    clubUnavailable: '—',
    planSuffix: '· club plan',
    planNoLimit: '· per-user limit not defined',
    defaultName: 'User',
  },
};

const AccountMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { lang: sportsLang, setLang: setSportsLang } = useSportsLang();
  const { setLang: setGovLang } = useGovernanceLang();

  const lang = sportsLang;
  const L = I18N[lang] || I18N.pt;

  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [usageError, setUsageError] = useState(false);
  const wrapperRef = useRef(null);

  // Toggle idioma (consolida o que estava solto no rodape do sidebar)
  const toggleLang = useCallback(() => {
    const next = lang === 'pt' ? 'en' : 'pt';
    setSportsLang(next);
    setGovLang(next);
  }, [lang, setSportsLang, setGovLang]);

  // Carrega usage do user logado (cache de 60s no service)
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    getMyUsage()
      .then((data) => {
        if (!cancelled) {
          setUsage(data);
          setUsageError(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[AccountMenu] failed to load /me/usage:', err?.message || err);
          setUsageError(true);
        }
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Fecha o menu ao clicar fora
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const handleLogout = () => {
    if (window.confirm(L.confirmLogout)) {
      setOpen(false);
      logout();
      navigate('/login');
    }
  };

  const goAndClose = (path) => () => {
    setOpen(false);
    navigate(path);
  };

  // ── Derivados pra display ────────────────────────────────────────────────
  const displayName = user?.name || L.defaultName;
  // C2-adjacente (jul/2026): ausencia-nao-e-estado. Antes, `|| L.noClub`
  // afirmava "Sem clube vinculado" durante loading (usage=null pre-fetch,
  // 60s de timeout em cold start) E durante erro — mesma familia do
  // RBACContext:214 (sintetizar fato a partir de ausencia). Gestor real do
  // Acqua lia "sem clube" a cada mount. Agora noClub SO quando o fetch
  // CONCLUIU (usage != null) e genuinamente nao ha clube.
  const clubName = usage?.club?.club_name
    || (usageError ? L.clubUnavailable : (usage ? L.noClub : L.loading));
  const hasSubscription = !!usage?.subscription;
  const ratioText = usage
    ? formatUsageRatio(usage.usage_current_month, usage.subscription)
    : L.loading;
  const planSuffix = hasSubscription ? L.planSuffix : L.planNoLimit;
  const avatarUrl = user?.avatar || usage?.user?.avatar_url;

  return (
    <div ref={wrapperRef} className="relative">
      {/* ─── BOTAO FECHADO (sempre visivel) ─── */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
        aria-expanded={open}
        aria-label="Abrir menu de conta"
      >
        {/* Avatar 32px */}
        <div className="w-9 h-9 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0 border border-gray-200">
          {avatarUrl ? (
            <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <User size={18} className="text-gray-400" />
          )}
        </div>
        {/* Bloco texto */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate leading-tight">
            {displayName}
          </div>
          <div className="text-xs font-medium text-gray-500 truncate leading-tight">
            {clubName}
          </div>
          {usage && !usageError && (
            <div className="text-[11px] text-gray-400 truncate leading-tight mt-0.5">
              {ratioText} <span className="text-gray-300">{planSuffix}</span>
            </div>
          )}
        </div>
        {/* Chevron up (indica que abre pra cima) */}
        <ChevronUp
          size={14}
          className={`text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* ─── DROPDOWN ASCENDENTE (abre PRA CIMA) ─── */}
      {open && (
        <div
          className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50"
          role="menu"
        >
          {/* Cabecalho */}
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-white flex items-center justify-center flex-shrink-0 border border-gray-200">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  <User size={24} className="text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {displayName}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {user?.email || ''}
                </div>
              </div>
            </div>
          </div>

          {/* Links */}
          <nav className="py-1">
            <button
              onClick={goAndClose('/configuracoes')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left"
            >
              <User size={16} className="text-gray-400" />
              <span>{L.viewProfile}</span>
            </button>
            <button
              onClick={goAndClose('/meu-consumo')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left"
            >
              <Activity size={16} className="text-gray-400" />
              <span>{L.myUsage}</span>
            </button>
            <button
              onClick={goAndClose('/configuracoes')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left"
            >
              <Settings size={16} className="text-gray-400" />
              <span>{L.settings}</span>
            </button>
            <button
              onClick={goAndClose('/contato')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left"
            >
              <HelpCircle size={16} className="text-gray-400" />
              <span>{L.help}</span>
            </button>

            {/* Idioma — consolida o toggle PT/EN que estava solto no sidebar */}
            <button
              onClick={toggleLang}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-left"
            >
              <Globe size={16} className="text-gray-400" />
              <span className="flex-1">{L.language}</span>
              <span className="text-xs font-semibold text-gray-500 uppercase">
                {lang}
              </span>
            </button>
          </nav>

          {/* Sair */}
          <div className="border-t border-gray-200 py-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-[#a93333] transition-colors text-left group"
            >
              <LogOut size={16} className="text-gray-400 group-hover:text-[#a93333]" />
              <span>{L.signOut}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
